import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { adminApi } from '@/lib/adminApi';
import { feedbackApi } from '@/lib/feedbackApi';
import { taskActionLabel, taskList, type Task } from '@/lib/tasks';
import { type ModeEntry } from '@/lib/modes';
import { AdminPage, RegistryTable, StatusPill, FeedbackControl } from '@/admin';

const REGISTRY = 'tasks';

type Tier = 'required' | 'recommended' | 'universal';
type Row = Task & { tier: Tier };

export function ModeTasksPage() {
  const { modeId = '' } = useParams();
  const qc = useQueryClient();
  const mq = useQuery({ queryKey: ['mode', modeId], queryFn: () => adminApi.modes.get(modeId) });
  const tq = useQuery({ queryKey: ['tasks'], queryFn: () => adminApi.tasks.list() });
  const pq = useQuery({ queryKey: ['panels'], queryFn: () => adminApi.panels.list() });
  const fb = useQuery({ queryKey: ['feedback-summary', REGISTRY], queryFn: () => feedbackApi.summary(REGISTRY) });

  const mode = (mq.data?.mode ?? {}) as ModeEntry;
  const allTasks = taskList(tq.data);
  const byId = new Map(allTasks.map((t) => [t.task_id, t]));
  const panels = pq.data?.panels ?? {};
  const refresh = () => void qc.invalidateQueries({ queryKey: ['feedback-summary', REGISTRY] });

  // The mode's task set: required + recommended (+ universal tasks if opted in).
  const modeTasks = mode.tasks ?? {};
  const rows: Row[] = [];
  const seen = new Set<string>();
  const add = (id: string, tier: Tier) => {
    const t = byId.get(id);
    if (t && !seen.has(id)) { seen.add(id); rows.push({ ...t, tier }); }
  };
  for (const id of modeTasks.required ?? []) add(id, 'required');
  for (const id of modeTasks.recommended ?? []) add(id, 'recommended');
  if (modeTasks.include_universal) for (const t of allTasks) if (t.universal) add(t.task_id, 'universal');

  // Required-panel coverage: which tasks fill each of the mode's required panels.
  const requiredPanels = mode.panels?.required ?? [];
  const coverage = requiredPanels.map((pid) => ({
    panelId: pid,
    label: panels[pid]?.label ?? pid,
    tasks: rows.filter((t) => (t.satisfies_panels ?? []).includes(pid)),
  }));

  const tierPill = (t: Tier) => t === 'required' ? <StatusPill on label="required" /> : <StatusPill on={false} label={t} />;

  return (
    <AdminPage
      title={`${mode.display_name || modeId} · Tasks`}
      description="What the user does to become mode-ready — each task fills one or more panels. The coverage panel below shows which task completes each required panel."
      backTo={{ to: `/admin/modes/${encodeURIComponent(modeId)}`, label: mode.display_name || modeId }}
    >
      <RegistryTable<Row>
        columns={[
          { key: 'task', header: 'Task', render: (t) => (
            <div>
              <div className="font-semibold">{t.label}</div>
              <div className="font-mono text-[11px] text-text-muted">{t.task_id}</div>
              {t.why ? <div className="mt-1 max-w-[420px] text-[12px] text-text-secondary">{t.why}</div> : null}
            </div>
          ) },
          { key: 'tier', header: 'Tier', render: (t) => tierPill(t.tier) },
          { key: 'category', header: 'Category', className: 'text-[12px] text-text-secondary', render: (t) => t.category || '—' },
          { key: 'action', header: 'Action', className: 'font-mono text-[11px] text-text-secondary', render: (t) => taskActionLabel(t) },
          { key: 'panels', header: 'Satisfies panels', render: (t) => (
            <div className="flex flex-wrap gap-1">
              {(t.satisfies_panels ?? []).map((pid) => (
                <Link key={pid} to={`/admin/panels/${encodeURIComponent(pid)}`} className="rounded-pill border border-border px-2 py-0.5 text-[11px] text-accent hover:bg-surface-sunken">{panels[pid]?.label ?? pid}</Link>
              ))}
              {(t.satisfies_panels ?? []).length === 0 ? <span className="text-text-muted text-[12px]">—</span> : null}
            </div>
          ) },
        ]}
        rows={rows}
        getRowKey={(t) => t.task_id}
        count={rows.length}
        state={{ loading: mq.isLoading || tq.isLoading, error: mq.isError || tq.isError }}
        emptyText="This mode declares no tasks."
        rowActions={(t) => (
          <FeedbackControl registry={REGISTRY} itemId={t.task_id} itemLabel={t.label} openCount={fb.data?.[t.task_id]?.open ?? 0} onChanged={refresh} />
        )}
      />

      {requiredPanels.length ? (
        <section className="mt-21">
          <h2 className="mb-8 text-h3 font-semibold">Required-panel coverage</h2>
          <p className="mb-13 text-body-sm text-text-secondary">Every required panel must be satisfied for the mode to be ready. A panel with no task is filled by upload / manual entry.</p>
          <div className="divide-y divide-border rounded border border-border bg-surface">
            {coverage.map((c) => (
              <div key={c.panelId} className="flex items-start justify-between gap-13 p-13">
                <Link to={`/admin/panels/${encodeURIComponent(c.panelId)}`} className="font-medium text-accent hover:underline">{c.label}</Link>
                <div className="flex flex-wrap justify-end gap-1">
                  {c.tasks.length ? c.tasks.map((t) => (
                    <span key={t.task_id} className="rounded-pill bg-success-soft px-2 py-0.5 text-[11px] font-semibold text-success">{t.label}</span>
                  )) : <span className="rounded-pill bg-surface-sunken px-2 py-0.5 text-[11px] text-text-secondary">upload / manual</span>}
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </AdminPage>
  );
}
